/* ===============================
   BASIC HELPERS
================================ */

export const isNonEmptyText = (value: string, minLength = 2): boolean => {
  return value.trim().length >= minLength;
};

/**
 * Used ONLY for human-like sentences / names
 * (website name, content text, rewrite input)
 */
export const isMeaningfulText = (value: string): boolean => {
  const text = value.toLowerCase().trim();

  // must contain at least one vowel
  if (!/[aeiou]/.test(text)) return false;

  // block long consonant chains (gfhgh, sdfsdf)
  if (/[bcdfghjklmnpqrstvwxyz]{5,}/i.test(text)) return false;

  // minimum length
  if (text.length < 4) return false;

  // block repeated patterns like sdfsdf
  if (/(\w{2,})\1+/.test(text)) return false;

  return true;
};

/* ===============================
   URL & DOMAIN
================================ */

export const normalizeUrl = (url: string): string => {
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return `https://${url}`;
  }
  return url;
};

export const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(normalizeUrl(url));
    return ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
};

export const isValidDomain = (url: string): boolean => {
  const domainRegex =
    /^(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;
  return domainRegex.test(url);
};

/**
 * Single source of truth for website URL validation
 */
export const isValidWebsiteUrl = (url: string): boolean => {
  if (!isNonEmptyText(url)) return false;
  if (!isValidDomain(url)) return false;
  return isValidUrl(url);
};

/* ===============================
   KEYWORDS
================================ */

/**
 * Keywords are NOT sentences
 * Example: "seo tools, keyword research, backlinks"
 */
export const isValidKeywordsInput = (keywords: string): boolean => {
  const list = keywords
    .split(",")
    .map(k => k.trim())
    .filter(Boolean);

  return (
    list.length > 0 &&
    list.every(
      k =>
        k.length >= 2 &&
        !/(\w)\1{3,}/.test(k) // block aaa, sssss
    )
  );
};

/* ===============================
   HEADER / FORM FIELDS
================================ */

export const isValidWebsiteName = (value: string): boolean => {
  return isNonEmptyText(value, 2) && isMeaningfulText(value);
};

export const isValidCreativity = (value: string): boolean => {
  return ["Regular", "High"].includes(value);
};

/* ===============================
   AI TEXT INPUT (Rewrite tools)
================================ */

export const isValidRewriteText = (text: string): boolean => {
  if (text.trim().length < 20) return false;
  return isMeaningfulText(text);
};

/* ===============================
   META TAG KEYWORDS
================================ */

export const isValidMetaKeywords = (keywords: string): boolean => {
  const list = keywords
    .split(",")
    .map(k => k.trim())
    .filter(Boolean);

  return (
    list.length > 0 &&
    list.every(k =>
      k.length >= 2 &&
      /[aeiou]/i.test(k) &&                  // must contain vowel
      !/[bcdfghjklmnpqrstvwxyz]{5,}/i.test(k) // block consonant spam
    )
  );
};

/* ===============================
   ARTICLE / CONTENT INPUT
================================ */

export const validateArticleContent = (
  text: string,
  minLength = 100
): string | null => {
  const value = text.trim();

  if (value.length < minLength) {
    return `Please enter at least ${minLength} characters.`;
  }

  // must contain vowels
  if (!/[aeiou]/i.test(value)) {
    return "Content must contain meaningful words.";
  }

  // block long consonant chains
  if (/[bcdfghjklmnpqrstvwxyz]{6,}/i.test(value)) {
    return "Content looks like random text. Please enter real sentences.";
  }

  // block repeated patterns
  if (/(\b\w{2,}\b)\s*\1\s*\1/i.test(value)) {
    return "Please avoid repeated or meaningless text.";
  }

  return null;
};


/* ===============================
   HOOKS GENERATOR
================================ */

export const validateHookTopic = (value: string): string | null => {
  const text = value.trim();

  if (!text) return "Topic is required.";
  if (text.length < 10) return "Topic should be at least 10 characters.";
  if (!/[aeiou]/i.test(text)) return "Topic must contain meaningful words.";

  return null;
};

export const validateTargetAudience = (value: string): string | null => {
  const text = value.trim();

  if (!text) return "Target Audience is required.";
  if (text.length < 3) return "Target Audience is too short.";

  return null;
};
